import { useEffect } from "react";

/**
 * Centralized keyboard navigation for the keynote.
 * Bindings:
 *   ArrowRight / Space / N / PageDown → next
 *   ArrowLeft / P / PageUp           → prev
 *   Home                              → first
 *   End                               → last
 *   G / Tab                           → toggle presenter (overview)
 *   F                                 → toggle fullscreen
 *   O                                 → open Presenter View (second window with notes)
 *   Escape                            → exit presenter mode (handled in component)
 */
export default function useKeyboard({
  onNext,
  onPrev,
  onFirst,
  onLast,
  onTogglePresenter,
  onToggleFullscreen,
  onOpenPresenterView,
  onEscape,
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e) => {
      // Avoid hijacking when typing inside fields
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) {
        return;
      }

      switch (e.key) {
        case "ArrowRight":
        case " ":
        case "n":
        case "N":
        case "PageDown":
          e.preventDefault();
          onNext?.();
          break;
        case "ArrowLeft":
        case "p":
        case "P":
        case "PageUp":
          e.preventDefault();
          onPrev?.();
          break;
        case "Home":
          e.preventDefault();
          onFirst?.();
          break;
        case "End":
          e.preventDefault();
          onLast?.();
          break;
        case "g":
        case "G":
        case "Tab":
          e.preventDefault();
          onTogglePresenter?.();
          break;
        case "f":
        case "F":
          e.preventDefault();
          onToggleFullscreen?.();
          break;
        case "o":
        case "O":
          e.preventDefault();
          onOpenPresenterView?.();
          break;
        case "Escape":
          e.preventDefault();
          onEscape?.();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, onNext, onPrev, onFirst, onLast, onTogglePresenter, onToggleFullscreen, onOpenPresenterView, onEscape]);
}
